import { useMemo, useState } from 'react';
import type { PlatformKey, StoreResponse, BrandResponse, ClientResponse } from '@clarity/contract';
import {
  useBrands,
  useClients,
  useStores,
  useDiscoverBrands,
  useImportBrand,
  useEnableStore,
  useDisableStore,
  useUnassignStore,
  useRemoveStore,
} from '../queries/index.js';
import { useClientContext } from '../lib/clientContext.js';
import { useSession } from '../lib/session.js';
import { QueryState } from './QueryState.js';
import { IconBrands } from '../components/icons.js';

const PLATFORMS: readonly PlatformKey[] = ['talabat', 'careem'];

/**
 * Brands (brand-model). Two surfaces, one component, split by where it is shown:
 *
 * - ADMIN LEVEL (no client selected): the DISCOVERY + IMPORT hub. The admin picks
 *   a target client and platform, discovers chains on the linked account, and
 *   imports them (brand + its outlets as branches). Below, an overview of every
 *   brand grouped by client. Discovery lives here — at the admin level — not in a
 *   client's workspace.
 * - CLIENT WORKSPACE (inside a client): manage THAT client's brands and branches
 *   (enable / disable). No discovery here.
 *
 * A brand belongs to exactly one client (Req 3.1) and one platform (R1). The
 * server enforces tenancy (Req 2.3).
 */
export function Brands() {
  const { inClientContext, activeClientId, selectedClient } = useClientContext();

  const brands = useBrands();
  const stores = useStores();

  const allBrands: BrandResponse[] = brands.data?.status === 200 ? brands.data.body : [];

  // Branches grouped under their owning brand.
  const branchesByBrand = useMemo(() => {
    const storeRows: StoreResponse[] = stores.data?.status === 200 ? stores.data.body : [];
    const m = new Map<string, StoreResponse[]>();
    for (const s of storeRows) {
      const list = m.get(s.brandId) ?? [];
      list.push(s);
      m.set(s.brandId, list);
    }
    return m;
  }, [stores.data]);

  if (inClientContext) {
    const clientBrands = allBrands.filter((b) => b.clientId === activeClientId);
    return (
      <section>
        <div className="page-head">
          <div>
            <h2>Brands</h2>
            <p className="sub">
              {selectedClient ? `${selectedClient.name} · ` : ''}
              Manage this client&apos;s brands and their branches.
            </p>
          </div>
        </div>
        <QueryState isLoading={brands.isLoading} isError={brands.isError} label="brands">
          {clientBrands.length === 0 ? (
            <EmptyBrands inClient />
          ) : (
            <div className="brand-stack">
              {clientBrands.map((brand) => (
                <BrandCard
                  key={brand.id}
                  brand={brand}
                  branches={branchesByBrand.get(brand.id) ?? []}
                />
              ))}
            </div>
          )}
        </QueryState>
      </section>
    );
  }

  // Admin level: discovery/import hub + overview grouped by client.
  return <AdminBrands brands={brands} allBrands={allBrands} branchesByBrand={branchesByBrand} />;
}

/* ── Admin level — discover/import + grouped overview ──────────────────────── */

function AdminBrands({
  brands,
  allBrands,
  branchesByBrand,
}: {
  brands: ReturnType<typeof useBrands>;
  allBrands: BrandResponse[];
  branchesByBrand: Map<string, StoreResponse[]>;
}) {
  const clients = useClients();
  const clientRows: ClientResponse[] = clients.data?.status === 200 ? clients.data.body : [];
  const clientById = useMemo(
    () => new Map(clientRows.map((c) => [c.id, c])),
    // clientRows is derived from clients.data; key the memo on the source.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clients.data],
  );

  // Group brands by owning client for the overview.
  const grouped = useMemo(() => {
    const m = new Map<string, BrandResponse[]>();
    for (const b of allBrands) {
      const list = m.get(b.clientId) ?? [];
      list.push(b);
      m.set(b.clientId, list);
    }
    return [...m.entries()];
  }, [allBrands]);

  return (
    <section>
      <div className="page-head">
        <div>
          <h2>Brands</h2>
          <p className="sub">Discover and import brands from a linked platform account, then review every brand across your clients.</p>
        </div>
      </div>

      <ImportPanel clients={clientRows} />

      <QueryState isLoading={brands.isLoading} isError={brands.isError} label="brands">
        {allBrands.length === 0 ? (
          <EmptyBrands />
        ) : (
          <div className="brand-overview">
            {grouped.map(([clientId, clientBrands]) => (
              <div className="brand-group" key={clientId}>
                <h3 className="brand-group__client">
                  {clientById.get(clientId)?.name ?? 'Unknown client'}
                  <span className="brand-group__count">
                    {clientBrands.length} brand{clientBrands.length === 1 ? '' : 's'}
                  </span>
                </h3>
                <div className="brand-stack">
                  {clientBrands.map((brand) => (
                    <BrandCard
                      key={brand.id}
                      brand={brand}
                      branches={branchesByBrand.get(brand.id) ?? []}
                      readOnly
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </QueryState>
    </section>
  );
}

/* ── Import panel (admin level) ────────────────────────────────────────────── */

function ImportPanel({ clients }: { clients: ClientResponse[] }) {
  const importBrand = useImportBrand();

  const [targetClientId, setTargetClientId] = useState('');
  const [platformKey, setPlatformKey] = useState<PlatformKey>('talabat');
  const [discovering, setDiscovering] = useState(false);

  const ready = targetClientId !== '';
  const discovery = useDiscoverBrands(platformKey, targetClientId, discovering && ready);
  const discovered = discovery.data?.status === 200 ? discovery.data.body.brands : [];
  const connection = discovery.data?.status === 200 ? discovery.data.body.connection : undefined;

  function onImport(brand: (typeof discovered)[number]) {
    importBrand.mutate({
      body: {
        clientId: targetClientId,
        platformKey,
        chainId: brand.chainId,
        name: brand.name,
        ...(brand.grid !== undefined && { grid: brand.grid }),
        branches: brand.branches,
      },
    });
  }

  return (
    <form className="card" aria-label="Discover and import brands" onSubmit={(e) => e.preventDefault()}>
      <h3>Discover &amp; import</h3>
      <p className="muted">
        Choose which client to import into, then pull its chains from the linked platform account.
      </p>

      <div className="field-row">
        <label>
          Client
          <select value={targetClientId} onChange={(e) => { setTargetClientId(e.target.value); setDiscovering(false); }} required>
            <option value="">Select a client…</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Platform
          <select
            value={platformKey}
            onChange={(e) => { setPlatformKey(e.target.value as PlatformKey); setDiscovering(false); }}
          >
            {PLATFORMS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="primary discover__btn"
          onClick={() => setDiscovering(true)}
          disabled={!ready || (discovery.isFetching && discovering)}
        >
          {discovery.isFetching && discovering ? 'Discovering…' : 'Discover brands'}
        </button>
      </div>

      {discovering && discovery.isSuccess && connection === 'disconnected' && (
        <p role="alert" className="error">
          No platform session — open and log into the portal in the linked Chrome.
        </p>
      )}
      {discovering && discovery.isSuccess && connection === 'expired' && (
        <p role="alert" className="error">
          Couldn’t read the account from the platform. The session is connected, but automatic
          brand discovery isn’t available yet — add the brand manually below for now.
        </p>
      )}
      {discovering && discovery.isSuccess && connection === 'connected' && discovered.length === 0 && (
        <p className="muted">No brands found for the linked account.</p>
      )}
      {discovering && discovery.isError && (
        <p role="alert" className="error">
          Discovery failed. Try again.
        </p>
      )}

      {discovered.length > 0 && (
        <ul className="discovered-list" aria-label="Discovered brands">
          {discovered.map((brand) => (
            <li key={brand.chainId}>
              <div className="discovered-list__info">
                <strong>{brand.name}</strong>
                <span className="muted">
                  {brand.branches.length} branch{brand.branches.length === 1 ? '' : 'es'}
                </span>
              </div>
              <button
                type="button"
                className={brand.alreadyImported ? 'sm ghost' : 'sm primary'}
                onClick={() => onImport(brand)}
                disabled={brand.alreadyImported || importBrand.isPending}
              >
                {brand.alreadyImported ? 'Imported' : 'Import'}
              </button>
            </li>
          ))}
        </ul>
      )}
      {importBrand.isError && (
        <p role="alert" className="error">
          Could not import the brand.
        </p>
      )}
    </form>
  );
}

/* ── Brand card — header + nested branch rows (proper hierarchy) ───────────── */

function BrandCard({
  brand,
  branches,
  readOnly = false,
}: {
  brand: BrandResponse;
  branches: StoreResponse[];
  readOnly?: boolean;
}) {
  return (
    <article className="card brand-card">
      <header className="brand-card__head">
        <div className="brand-card__title">
          <h3>{brand.name}</h3>
          <span className="badge">{brand.platformKey}</span>
        </div>
        <span className="brand-card__meta">
          {branches.length} branch{branches.length === 1 ? '' : 'es'}
        </span>
      </header>

      {branches.length === 0 ? (
        <p className="muted brand-card__empty">No branches imported for this brand yet.</p>
      ) : (
        <ul className="branch-list" aria-label={`${brand.name} branches`}>
          {branches.map((store) => (
            <BranchRow key={store.id} store={store} readOnly={readOnly} />
          ))}
        </ul>
      )}
    </article>
  );
}

function BranchRow({ store, readOnly }: { store: StoreResponse; readOnly: boolean }) {
  const { role } = useSession();
  const enableStore = useEnableStore();
  const disableStore = useDisableStore();
  const unassignStore = useUnassignStore();
  const removeStore = useRemoveStore();

  // Unassign reverses an assign for a never-enabled branch (assigned → removed).
  // It is an ADMIN-ONLY action (the server returns 403 to a client-role caller),
  // so only an admin ever sees the button. Beyond the role gate it is offered
  // only while the branch is still `assigned`; once enabled, the branch must be
  // disabled (which keeps data through the 30-day grace window) instead.
  const canUnassign = role === 'admin' && store.state === 'assigned';
  // Delete hard-removes a REMOVED branch's row so a re-import recreates it fresh
  // (unassign only tombstones it to `removed`, which a re-import would skip).
  // ADMIN-ONLY (server 403s a client) and offered only once the branch is
  // `removed` — a live branch must be unassigned / disabled first.
  const canRemove = role === 'admin' && store.state === 'removed';
  // Enable / Disable are admin-safe lifecycle actions (the server gates them to
  // admins and refuses a client), so an admin may run them from the read-only
  // overview too — not just inside a client's workspace. A client-role user only
  // ever sees them in its own workspace (where readOnly is false).
  const canManage = !readOnly || role === 'admin';
  const busy =
    enableStore.isPending ||
    disableStore.isPending ||
    unassignStore.isPending ||
    removeStore.isPending;

  return (
    <li className="branch-row">
      <div className="branch-row__id">
        <span className="branch-row__name">{store.name ?? store.externalStoreId}</span>
        {store.name && <span className="branch-row__ext muted">#{store.externalStoreId}</span>}
      </div>
      <span className={`badge badge-${store.state}`}>{store.state}</span>
      {canManage && (
        <div className="branch-row__actions">
          <button
            type="button"
            className="sm"
            onClick={() => enableStore.mutate({ params: { id: store.id }, body: {} })}
            disabled={busy || store.state === 'enabled' || store.state === 'removed'}
          >
            Enable
          </button>
          <button
            type="button"
            className="sm ghost"
            onClick={() => disableStore.mutate({ params: { id: store.id }, body: {} })}
            disabled={busy || store.state !== 'enabled'}
          >
            Disable
          </button>
          {canUnassign && (
            <button
              type="button"
              className="sm danger"
              onClick={() => unassignStore.mutate({ params: { id: store.id }, body: {} })}
              disabled={busy}
              title="Remove this assignment (reverses assign; only for never-enabled branches)"
            >
              {unassignStore.isPending ? 'Unassigning…' : 'Unassign'}
            </button>
          )}
          {canRemove && (
            <button
              type="button"
              className="sm danger"
              onClick={() => removeStore.mutate({ params: { id: store.id }, body: {} })}
              disabled={busy}
              title="Delete this removed branch for good, so it can be re-imported fresh"
            >
              {removeStore.isPending ? 'Deleting…' : 'Delete'}
            </button>
          )}
        </div>
      )}
      {removeStore.isError && (
        <p role="alert" className="error">
          Could not delete this branch.
        </p>
      )}
    </li>
  );
}

function EmptyBrands({ inClient = false }: { inClient?: boolean }) {
  return (
    <div className="card empty">
      <IconBrands className="empty__glyph" />
      <p>
        {inClient
          ? 'No brands for this client yet. An admin can discover and import brands from the Brands hub.'
          : 'No brands across your clients yet. Select a client above and discover brands to get started.'}
      </p>
    </div>
  );
}
